import React, { useState, useEffect, useRef, useCallback } from 'react';
import { Icon } from '@iconify/react';
import { loadGoogleMaps } from '../../helper/googleMapsLoader';
import { loadLeaflet } from '../../helper/leafletLoader';
import config from '../../config';

// Default map center (Downtown Dubai, UAE)
const DEFAULT_CENTER = { lat: 25.2048, lng: 55.2708 };
const DEFAULT_ZOOM = 13;

// UAE Quick City Jump Presets
const UAE_CITIES = [
    { name: 'Dubai',          lat: 25.2048, lng: 55.2708, zoom: 13 },
    { name: 'Abu Dhabi',      lat: 24.4539, lng: 54.3773, zoom: 13 },
    { name: 'Sharjah',        lat: 25.3463, lng: 55.4209, zoom: 13 },
    { name: 'Ajman',          lat: 25.4052, lng: 55.5136, zoom: 13 },
    { name: 'RAK',            lat: 25.7895, lng: 55.9432, zoom: 13 },
    { name: 'Fujairah',       lat: 25.1288, lng: 56.3265, zoom: 13 },
    { name: 'Al Ain',         lat: 24.2075, lng: 55.7447, zoom: 13 },
];

// City-tailored default street, area and landmark suggestions
export const CITY_DEFAULT_SUGGESTIONS = {
    'Dubai': [
        { name: 'Downtown Dubai / Sheikh Zayed Rd', street: 'Sheikh Zayed Road, Downtown Dubai', lat: 25.1972, lng: 55.2744, area: 'Downtown / Business Bay' },
        { name: 'Dubai Marina Walk', street: 'Dubai Marina Walk, Marsa Dubai', lat: 25.0784, lng: 55.1388, area: 'Dubai Marina' },
        { name: 'Business Bay (Marasi Drive)', street: 'Marasi Drive, Business Bay', lat: 25.1847, lng: 55.2801, area: 'Business Bay' },
        { name: 'Palm Jumeirah / The Boardwalk', street: 'Crescent Road, Palm Jumeirah', lat: 25.1305, lng: 55.1172, area: 'Palm Jumeirah' },
        { name: 'DIFC (Gate Village)', street: 'Trade Centre 2, DIFC Gate Village', lat: 25.2106, lng: 55.2805, area: 'Financial Centre' },
        { name: 'JBR - The Walk', street: 'The Walk, Jumeirah Beach Residence', lat: 25.0805, lng: 55.1345, area: 'JBR' },
        { name: 'City Walk / Al Wasl', street: 'Al Safa Street, City Walk, Al Wasl', lat: 25.2078, lng: 55.2618, area: 'Al Wasl / Jumeirah' },
        { name: 'JLT (Jumeirah Lakes Towers)', street: 'Cluster D, Jumeirah Lake Towers', lat: 25.0743, lng: 55.1489, area: 'JLT' },
        { name: 'Al Barsha 1 (Near Mall of Emirates)', street: 'Al Barsha 1, Street 12', lat: 25.1181, lng: 55.2014, area: 'Al Barsha' },
        { name: 'Deira / Rigga Street', street: 'Al Rigga Road, Deira', lat: 25.2633, lng: 55.3236, area: 'Deira' }
    ],
    'Abu Dhabi': [
        { name: 'Corniche Road', street: 'Corniche Road West, Al Khubeirah', lat: 24.4712, lng: 54.3411, area: 'Corniche' },
        { name: 'Yas Island (Yas Marina / Bay)', street: 'Yas Leisure Drive, Yas Marina', lat: 24.4988, lng: 54.6062, area: 'Yas Island' },
        { name: 'Al Maryah Island (ADGM Square)', street: 'Al Maryah Island, Global Market', lat: 24.5008, lng: 54.3887, area: 'Financial District' },
        { name: 'Saadiyat Island (Cultural District)', street: 'Jacques Chirac St, Saadiyat Cultural District', lat: 24.5338, lng: 54.3982, area: 'Saadiyat Island' },
        { name: 'Al Reem Island (Shams)', street: 'Shams Abu Dhabi, Al Reem Island', lat: 24.4975, lng: 54.4072, area: 'Al Reem' },
        { name: 'Khalifa City', street: 'Sector 12, Khalifa City A', lat: 24.4253, lng: 54.5826, area: 'Khalifa City' }
    ],
    'Sharjah': [
        { name: 'Al Majaz Waterfront', street: 'Corniche Street, Al Majaz 2', lat: 25.3315, lng: 55.3889, area: 'Al Majaz' },
        { name: 'Al Qasba Canal', street: 'Al Khan Street, Al Qasba', lat: 25.3217, lng: 55.3756, area: 'Al Qasba' },
        { name: 'University City Road (Muwaileh)', street: 'University City Road, Muwaileh Commercial', lat: 25.3051, lng: 55.4528, area: 'Muwaileh' },
        { name: 'Al Khan Corniche', street: 'Al Khan Beach Sub-Street', lat: 25.3375, lng: 55.3615, area: 'Al Khan' }
    ],
    'Ajman': [
        { name: 'Ajman Corniche', street: 'Sheikh Humaid Bin Rashid Al Nuaimi St, Ajman Corniche', lat: 25.4168, lng: 55.4385, area: 'Corniche' },
        { name: 'Al Nuaimiya', street: 'Kuwait Street, Al Nuaimiya 1', lat: 25.3912, lng: 55.4561, area: 'Al Nuaimiya' },
        { name: 'Al Rashidiya', street: 'Sheikh Khalifa Bin Zayed St, Al Rashidiya', lat: 25.4021, lng: 55.4489, area: 'Al Rashidiya' }
    ],
    'Ras Al Khaimah': [
        { name: 'Al Marjan Island', street: 'Al Marjan Island Blvd', lat: 25.6706, lng: 55.7412, area: 'Al Marjan Island' },
        { name: 'Al Hamra Village', street: 'Al Hamra Village Marina', lat: 25.6888, lng: 55.7824, area: 'Al Hamra' },
        { name: 'Khuzam / Corniche Al Qawasim', street: 'Al Qawasim Corniche Rd, Khuzam', lat: 25.7781, lng: 55.9452, area: 'Corniche' }
    ],
    'RAK': [
        { name: 'Al Marjan Island', street: 'Al Marjan Island Blvd', lat: 25.6706, lng: 55.7412, area: 'Al Marjan Island' },
        { name: 'Al Hamra Village', street: 'Al Hamra Village Marina', lat: 25.6888, lng: 55.7824, area: 'Al Hamra' },
        { name: 'Khuzam / Corniche Al Qawasim', street: 'Al Qawasim Corniche Rd, Khuzam', lat: 25.7781, lng: 55.9452, area: 'Corniche' }
    ],
    'Fujairah': [
        { name: 'Hamad Bin Abdulla Road', street: 'Hamad Bin Abdulla Road, Central Fujairah', lat: 25.1221, lng: 56.3268, area: 'City Center' },
        { name: 'Fujairah Corniche', street: 'Corniche Road, Al Faseel', lat: 25.1396, lng: 56.3572, area: 'Corniche' }
    ],
    'Al Ain': [
        { name: 'Khalifa Bin Zayed Street', street: 'Khalifa Bin Zayed Al Awwal St, Central District', lat: 24.2235, lng: 55.7612, area: 'Downtown' },
        { name: 'Al Jimi (Mall Area)', street: 'Hamdan Bin Mohammad St, Al Jimi', lat: 24.2541, lng: 55.7483, area: 'Al Jimi' },
        { name: 'Jebel Hafeet Foothills', street: 'Green Mubazzarah St, Al Ain', lat: 24.0982, lng: 55.7495, area: 'Green Mubazzarah' }
    ]
};

// Escape HTML for safe use inside InfoWindow / Popup content
const escapeHtml = (str) => {
    if (!str) return '';
    return String(str)
        .replace(/&/g, '&amp;')
        .replace(/</g, '&lt;')
        .replace(/>/g, '&gt;')
        .replace(/"/g, '&quot;')
        .replace(/'/g, '&#039;');
};

// Check if coordinates fall inside the old uninitialized Um Laylah desert default (~24.0, 54.0)
const isLegacyDesertDefault = (lat, lng) => {
    if (typeof lat !== 'number' || typeof lng !== 'number' || isNaN(lat) || isNaN(lng)) return true;
    return lat >= 23.75 && lat <= 24.15 && lng >= 53.80 && lng <= 54.15;
};

// Parse Google Geocoding / Places (Legacy or New) address_components into structured address
const parseGoogleAddressComponents = (components = [], formattedAddress = '') => {
    const address = {
        street: '',
        city: '',
        state: '',
        zip: '',
        country: ''
    };

    let streetNumber = '';
    let route = '';
    let neighborhood = '';
    let sublocality = '';
    let premise = '';

    components.forEach(comp => {
        const types = comp.types || [];
        const longName = comp.long_name || comp.longText || '';
        if (!longName) return;

        if (types.includes('street_number')) streetNumber = longName;
        if (types.includes('route')) route = longName;
        if (types.includes('premise') || types.includes('establishment') || types.includes('point_of_interest')) {
            if (!premise) premise = longName;
        }
        if (types.includes('neighborhood')) neighborhood = longName;
        if (types.includes('sublocality') || types.includes('sublocality_level_1')) sublocality = longName;
        if (types.includes('locality')) address.city = longName;
        if (types.includes('administrative_area_level_1')) address.state = longName;
        if (types.includes('postal_code')) address.zip = longName;
        if (types.includes('country')) address.country = longName;
    });

    if (!address.city && address.state) {
        address.city = address.state;
    }

    const streetParts = [streetNumber, route].filter(Boolean);
    if (streetParts.length > 0) {
        address.street = streetParts.join(' ');
        if (sublocality && !address.street.includes(sublocality)) {
            address.street = `${address.street}, ${sublocality}`;
        }
    } else {
        address.street = [premise, neighborhood, sublocality].filter(Boolean).join(', ') || (formattedAddress ? formattedAddress.split(',')[0] : '');
    }

    return address;
};

// Nominatim reverse geocode fallback
const reverseGeocodeNominatim = async (lat, lng) => {
    try {
        const url = `https://nominatim.openstreetmap.org/reverse?format=json&lat=${lat}&lon=${lng}&addressdetails=1&accept-language=en`;
        const res = await fetch(url, { headers: { 'Accept': 'application/json' } });
        if (!res.ok) return null;
        const data = await res.json();
        if (!data?.address) return null;
        const a = data.address;
        const streetParts = [
            a.house_number,
            a.road || a.pedestrian || a.footway || a.suburb || a.neighbourhood
        ].filter(Boolean);
        return {
            street: streetParts.join(' ') || data.name || '',
            city: a.city || a.town || a.village || a.municipality || a.county || '',
            state: a.state || a.region || '',
            zip: a.postcode || '',
            country: a.country || ''
        };
    } catch (e) {
        return null;
    }
};

// Reverse geocode using Google Maps REST API first, then JS Geocoder, then Nominatim
const reverseGeocodeLocation = async (lat, lng) => {
    if (typeof lat !== 'number' || typeof lng !== 'number' || isNaN(lat) || isNaN(lng)) {
        return null;
    }

    if (config.GOOGLE_MAPS_API_KEY) {
        try {
            const url = `https://maps.googleapis.com/maps/api/geocode/json?latlng=${lat},${lng}&key=${config.GOOGLE_MAPS_API_KEY}`;
            const res = await fetch(url);
            if (res.ok) {
                const data = await res.json();
                if (data.status === 'OK' && data.results?.[0]) {
                    return parseGoogleAddressComponents(data.results[0].address_components, data.results[0].formatted_address);
                }
            }
        } catch (err) {
            console.warn('Google REST Geocoding error:', err);
        }
    }

    if (window.google?.maps?.Geocoder) {
        try {
            const geocoder = new window.google.maps.Geocoder();
            const result = await new Promise((resolve) => {
                geocoder.geocode({ location: { lat, lng } }, (results, status) => {
                    if (status === 'OK' && results && results[0]) {
                        resolve(parseGoogleAddressComponents(results[0].address_components, results[0].formatted_address));
                    } else {
                        resolve(null);
                    }
                });
            });
            if (result && (result.street || result.city || result.country)) {
                return result;
            }
        } catch (err) {
            // fallback
        }
    }

    return await reverseGeocodeNominatim(lat, lng);
};

// Search places using Google Places API (New) v1 + Google Geocoding API
const searchGooglePlacesNew = async (query, biasLocation = null) => {
    const apiKey = config.GOOGLE_MAPS_API_KEY;
    if (!apiKey || !query?.trim()) return [];

    try {
        const body = {
            textQuery: query.trim(),
            maxResultCount: 6,
            languageCode: 'en'
        };
        if (biasLocation && typeof biasLocation.lat === 'number' && typeof biasLocation.lng === 'number') {
            body.locationBias = {
                circle: {
                    center: { latitude: biasLocation.lat, longitude: biasLocation.lng },
                    radius: 50000.0
                }
            };
        }

        const res = await fetch('https://places.googleapis.com/v1/places:searchText', {
            method: 'POST',
            headers: {
                'Content-Type': 'application/json',
                'X-Goog-Api-Key': apiKey,
                'X-Goog-FieldMask': 'places.id,places.displayName,places.formattedAddress,places.location,places.addressComponents,places.rating,places.primaryType'
            },
            body: JSON.stringify(body)
        });

        if (res.ok) {
            const data = await res.json();
            if (Array.isArray(data.places) && data.places.length > 0) {
                return data.places.map(p => ({
                    id: p.id,
                    name: p.displayName?.text || p.formattedAddress?.split(',')[0] || 'Location',
                    formatted_address: p.formattedAddress || '',
                    lat: p.location?.latitude,
                    lng: p.location?.longitude,
                    rating: p.rating || null,
                    category: p.primaryType ? p.primaryType.replace(/_/g, ' ') : '',
                    address: p.addressComponents
                        ? parseGoogleAddressComponents(p.addressComponents, p.formattedAddress)
                        : null
                })).filter(item => typeof item.lat === 'number' && typeof item.lng === 'number');
            }
        }
    } catch (e) {
        console.warn('Google Places (New) searchText error:', e);
    }

    try {
        const url = `https://maps.googleapis.com/maps/api/geocode/json?address=${encodeURIComponent(query.trim())}&key=${apiKey}`;
        const res = await fetch(url);
        if (res.ok) {
            const data = await res.json();
            if (data.status === 'OK' && Array.isArray(data.results) && data.results.length > 0) {
                return data.results.slice(0, 6).map(item => ({
                    id: item.place_id,
                    name: item.formatted_address?.split(',')[0] || 'Location',
                    formatted_address: item.formatted_address,
                    lat: item.geometry.location.lat,
                    lng: item.geometry.location.lng,
                    rating: null,
                    category: item.types?.[0]?.replace(/_/g, ' ') || '',
                    address: parseGoogleAddressComponents(item.address_components, item.formatted_address)
                }));
            }
        }
    } catch (e) {
        console.warn('Google Geocoding search error:', e);
    }

    return [];
};

// Fetch details for a single Google Place ID (when user clicks a native POI on Google Map)
const fetchGooglePlaceById = async (placeId) => {
    const apiKey = config.GOOGLE_MAPS_API_KEY;
    if (!apiKey || !placeId) return null;

    try {
        const res = await fetch(`https://places.googleapis.com/v1/places/${encodeURIComponent(placeId)}`, {
            headers: {
                'X-Goog-Api-Key': apiKey,
                'X-Goog-FieldMask': 'id,displayName,formattedAddress,location,addressComponents,rating,primaryType'
            }
        });
        if (res.ok) {
            const p = await res.json();
            if (p?.location) {
                return {
                    id: p.id,
                    name: p.displayName?.text || '',
                    formatted_address: p.formattedAddress || '',
                    lat: p.location.latitude,
                    lng: p.location.longitude,
                    address: p.addressComponents
                        ? parseGoogleAddressComponents(p.addressComponents, p.formattedAddress)
                        : null
                };
            }
        }
    } catch (e) {
        console.warn('Google Place details fetch error:', e);
    }
    return null;
};

// Parse coordinates or place info from any pasted Google Maps link, Plus Code, or coordinate string
const parseMapInputString = (rawInput) => {
    if (!rawInput || typeof rawInput !== 'string') return null;
    const text = rawInput.trim();
    if (!text) return null;

    // 1. Direct "lat, lng" or "lat lng" coordinate pair
    const directCoordMatch = text.match(/^(-?\d{1,2}\.\d+)\s*[,;\s]\s*(-?\d{1,3}\.\d+)$/);
    if (directCoordMatch) {
        const lat = parseFloat(directCoordMatch[1]);
        const lng = parseFloat(directCoordMatch[2]);
        if (lat >= -90 && lat <= 90 && lng >= -180 && lng <= 180) {
            return { type: 'coords', lat, lng };
        }
    }

    // 2. Exact Google Maps pin coordinates in URL: !3d25.2053662!4d55.2745985
    const bangMatch = text.match(/!3d(-?\d+\.\d+)!4d(-?\d+\.\d+)/);
    if (bangMatch) {
        const lat = parseFloat(bangMatch[1]);
        const lng = parseFloat(bangMatch[2]);
        let placeName = '';
        const placeMatch = text.match(/\/maps\/place\/([^/?@]+)/i);
        if (placeMatch?.[1]) {
            try { placeName = decodeURIComponent(placeMatch[1].replace(/\+/g, ' ')); } catch (_) {}
        }
        if (lat >= -90 && lat <= 90 && lng >= -180 && lng <= 180) {
            return { type: 'coords', lat, lng, placeName };
        }
    }

    // 3. Google Maps URL with @lat,lng
    const atMatch = text.match(/@(-?\d+\.\d+),(-?\d+\.\d+)/);
    if (atMatch) {
        const lat = parseFloat(atMatch[1]);
        const lng = parseFloat(atMatch[2]);
        let placeName = '';
        const placeMatch = text.match(/\/maps\/place\/([^/?@]+)/i);
        if (placeMatch?.[1]) {
            try { placeName = decodeURIComponent(placeMatch[1].replace(/\+/g, ' ')); } catch (_) {}
        }
        if (lat >= -90 && lat <= 90 && lng >= -180 && lng <= 180) {
            return { type: 'coords', lat, lng, placeName };
        }
    }

    // 4. URL query parameters: ?q=lat,lng | ?ll=lat,lng | ?query=lat,lng
    const queryCoordMatch = text.match(/[?&](?:q|ll|query|center|destination|daddr|saddr)=(-?\d+\.\d+)(?:,|%2C|\s)+(-?\d+\.\d+)/i);
    if (queryCoordMatch) {
        const lat = parseFloat(queryCoordMatch[1]);
        const lng = parseFloat(queryCoordMatch[2]);
        if (lat >= -90 && lat <= 90 && lng >= -180 && lng <= 180) {
            return { type: 'coords', lat, lng };
        }
    }

    // 5. Shortened Google Maps URL (maps.app.goo.gl or goo.gl/maps)
    if (/https?:\/\/(?:maps\.app\.goo\.gl|goo\.gl\/maps)\/[^\s]+/i.test(text)) {
        const urlMatch = text.match(/https?:\/\/[^\s]+/i);
        return { type: 'short_url', url: urlMatch[0] };
    }

    // 6. Full Google Maps URL without coordinates, but with /maps/place/NAME
    if (/google\.[a-z.]+\/maps/i.test(text)) {
        const placeMatch = text.match(/\/maps\/(?:place|search)\/([^/?@]+)/i);
        if (placeMatch?.[1]) {
            try {
                const placeName = decodeURIComponent(placeMatch[1].replace(/\+/g, ' '));
                return { type: 'query', query: placeName, url: text };
            } catch (_) {}
        }
        try {
            const u = new URL(text);
            const qParam = u.searchParams.get('q') || u.searchParams.get('query') || u.searchParams.get('destination');
            if (qParam) {
                return { type: 'query', query: qParam, url: text };
            }
        } catch (_) {}
        return { type: 'short_url', url: text };
    }

    return null;
};

// Build SVG data URL icon for Google Maps POI markers
const createGooglePoiIcon = (amenity) => {
    let pinColor = '#0789d5';
    let bgLight = '#dbeafe';
    let iconChar = '🍽';

    if (amenity === 'cafe') {
        pinColor = '#d97706';
        bgLight = '#fef3c7';
        iconChar = '☕';
    } else if (amenity === 'fast_food' || amenity === 'bar' || amenity === 'pub') {
        pinColor = '#8b5cf6';
        bgLight = '#ede9fe';
        iconChar = '🍹';
    }

    const svg = `<svg xmlns="http://www.w3.org/2000/svg" width="32" height="42" viewBox="0 0 32 42">
        <path d="M16 0C7.16 0 0 7.16 0 16C0 27.5 16 42 16 42S32 27.5 32 16C32 7.16 24.84 0 16 0Z" fill="${pinColor}"/>
        <circle cx="16" cy="15" r="10" fill="${bgLight}"/>
        <text x="16" y="20" text-anchor="middle" font-size="12" fill="${pinColor}">${iconChar}</text>
    </svg>`;

    return {
        url: 'data:image/svg+xml;charset=UTF-8,' + encodeURIComponent(svg),
        scaledSize: new window.google.maps.Size(30, 39),
        anchor: new window.google.maps.Point(15, 39)
    };
};

// Shared inline button style helper so global template CSS never distorts buttons
const btnBase = {
    display: 'inline-flex',
    alignItems: 'center',
    justifyContent: 'center',
    gap: '6px',
    whiteSpace: 'nowrap',
    cursor: 'pointer',
    fontWeight: 600,
    fontSize: '12px',
    lineHeight: 1,
    borderRadius: '8px',
    transition: 'all 0.15s ease',
    userSelect: 'none'
};

const MapPicker = ({
    initialLocation = null,
    initialAddress = null,
    selectedCity = '',
    onLocationSelect,
    height = '100%',
    enableReverseGeocoding = true
}) => {
    const mapContainerRef = useRef(null);
    const googleMapRef    = useRef(null);
    const leafletMapRef   = useRef(null);
    const leafletLibRef   = useRef(null);
    const poiMarkersRef   = useRef([]);
    const infoWindowRef   = useRef(null);
    const lastPoiFetchRef = useRef(null);
    const idleTimerRef    = useRef(null);
    const searchDebounceRef = useRef(null);
    const onLocationSelectRef = useRef(onLocationSelect);
    const isMountedRef    = useRef(true);
    const userInteractedRef = useRef(false);
    const isPinLockedRef  = useRef(false);
    const poiCategoryRef  = useRef('all');
    const omniboxWrapperRef = useRef(null);
    const hasInitialNotifiedRef = useRef(false);

    useEffect(() => {
        const handleKeyDown = (e) => {
            if (e.key === 'Escape') {
                setSearchResults([]);
                setIsOmniboxFocused(false);
                setOpenDrawer('none');
                setToolStatusMsg(null);
                if (infoWindowRef.current) {
                    infoWindowRef.current.close();
                }
                if (leafletMapRef.current?.closePopup) {
                    leafletMapRef.current.closePopup();
                }
            }
        };

        const handleMouseDown = (e) => {
            if (omniboxWrapperRef.current && !omniboxWrapperRef.current.contains(e.target)) {
                setSearchResults([]);
                setIsOmniboxFocused(false);
            }
        };

        document.addEventListener('keydown', handleKeyDown);
        document.addEventListener('mousedown', handleMouseDown);
        return () => {
            document.removeEventListener('keydown', handleKeyDown);
            document.removeEventListener('mousedown', handleMouseDown);
        };
    }, []);

    const hasValidInitial = initialLocation &&
        typeof initialLocation.lat === 'number' && !isNaN(initialLocation.lat) &&
        typeof initialLocation.lng === 'number' && !isNaN(initialLocation.lng) &&
        !isLegacyDesertDefault(initialLocation.lat, initialLocation.lng);

    const initLat = hasValidInitial ? initialLocation.lat : DEFAULT_CENTER.lat;
    const initLng = hasValidInitial ? initialLocation.lng : DEFAULT_CENTER.lng;

    const [mapEngine, setMapEngine] = useState('google'); // 'google' | 'leaflet'
    const [mapTypeId, setMapTypeId] = useState('roadmap'); // 'roadmap' | 'hybrid'
    const [isApiLoaded, setIsApiLoaded] = useState(false);
    const [location, setLocation] = useState({ lat: initLat, lng: initLng });
    const [isLoading, setIsLoading] = useState(true);
    const [geocodingAddress, setGeocodingAddress] = useState(null);
    const [selectedPlaceName, setSelectedPlaceName] = useState('');

    // Unified Smart Omnibox Search / Link State
    const [omniboxValue, setOmniboxValue] = useState('');
    const [searchResults, setSearchResults] = useState([]);
    const [isSearching, setIsSearching] = useState(false);
    const [isOmniboxFocused, setIsOmniboxFocused] = useState(false);

    // Resolve current active city suggestions
    const currentCityName = (selectedCity || initialAddress?.city || 'Dubai').trim();
    const matchedCityKey = Object.keys(CITY_DEFAULT_SUGGESTIONS).find(
        k => k.toLowerCase() === currentCityName.toLowerCase()
    ) || (currentCityName ? Object.keys(CITY_DEFAULT_SUGGESTIONS).find(
        k => k.toLowerCase().includes(currentCityName.toLowerCase()) || currentCityName.toLowerCase().includes(k.toLowerCase())
    ) : null) || 'Dubai';
    const cityDefaultSuggestions = CITY_DEFAULT_SUGGESTIONS[matchedCityKey] || CITY_DEFAULT_SUGGESTIONS['Dubai'] || [];

    // Optional Collapsible Panels: 'none' | 'link' | 'coords'
    const [openDrawer, setOpenDrawer] = useState('none');
    const [sharedLinkInput, setSharedLinkInput] = useState('');
    const [isResolvingLink, setIsResolvingLink] = useState(false);
    const [toolStatusMsg, setToolStatusMsg] = useState(null); // { type: 'success'|'danger'|'info', text: string }
    const [manualLat, setManualLat] = useState(initLat.toFixed(6));
    const [manualLng, setManualLng] = useState(initLng.toFixed(6));
    const [isPinLocked, setIsPinLocked] = useState(false);

    // Nearby POI state
    const [poiCategory, setPoiCategory] = useState('all');
    const [pois, setPois] = useState([]);
    const [isPoiLoading, setIsPoiLoading] = useState(false);

    useEffect(() => {
        onLocationSelectRef.current = onLocationSelect;
    }, [onLocationSelect]);

    useEffect(() => {
        poiCategoryRef.current = poiCategory;
    }, [poiCategory]);

    useEffect(() => {
        isPinLockedRef.current = isPinLocked;
    }, [isPinLocked]);

    useEffect(() => {
        return () => {
            isMountedRef.current = false;
            if (leafletMapRef.current) {
                leafletMapRef.current.remove();
                leafletMapRef.current = null;
            }
        };
    }, []);

    const showFeedback = useCallback((type, text, duration = 4500) => {
        if (!isMountedRef.current) return;
        setToolStatusMsg({ type, text });
        if (duration > 0) {
            setTimeout(() => {
                if (isMountedRef.current) {
                    setToolStatusMsg(prev => (prev?.text === text ? null : prev));
                }
            }, duration);
        }
    }, []);

    const switchToLeaflet = useCallback(() => {
        if (!isMountedRef.current) return;
        setMapEngine('leaflet');
        if (googleMapRef.current && mapContainerRef.current) {
            mapContainerRef.current.innerHTML = '';
            googleMapRef.current = null;
        }
    }, []);

    // Load Google Maps
    useEffect(() => {
        let cancelled = false;

        const prevAuthFailure = window.gm_authFailure;
        window.gm_authFailure = () => {
            if (prevAuthFailure) prevAuthFailure();
            if (!cancelled && isMountedRef.current) {
                switchToLeaflet();
            }
        };

        loadGoogleMaps()
            .then(() => {
                if (!cancelled && isMountedRef.current) {
                    setIsApiLoaded(true);
                }
            })
            .catch(() => {
                if (!cancelled && isMountedRef.current) {
                    switchToLeaflet();
                }
            });

        return () => {
            cancelled = true;
        };
    }, [switchToLeaflet]);

    // Update location state + notify parent
    const updateLocation = useCallback(async (lat, lng, customAddress = null, venueName = null, shouldNotifyAddress = true) => {
        if (!isMountedRef.current) return;
        const newLocation = { lat, lng };
        setLocation(newLocation);
        setManualLat(lat.toFixed(6));
        setManualLng(lng.toFixed(6));
        if (venueName) {
            setSelectedPlaceName(venueName);
        }

        let address = customAddress;
        if (!address && enableReverseGeocoding) {
            address = await reverseGeocodeLocation(lat, lng);
        }
        if (address && isMountedRef.current) {
            setGeocodingAddress(address);
        }
        if (onLocationSelectRef.current) {
            onLocationSelectRef.current(newLocation, shouldNotifyAddress ? address : null, venueName);
        }
    }, [enableReverseGeocoding]);

    // Fetch POIs using Google Places API (New) searchNearby first
    const fetchPoiList = useCallback(async (lat, lng, category) => {
        if (category === 'none') return [];

        const apiKey = config.GOOGLE_MAPS_API_KEY;
        if (apiKey) {
            try {
                let includedTypes = ['restaurant', 'cafe', 'meal_takeaway', 'bakery'];
                if (category === 'restaurant') includedTypes = ['restaurant'];
                else if (category === 'cafe') includedTypes = ['cafe', 'coffee_shop', 'bakery'];
                else if (category === 'fast_food') includedTypes = ['fast_food_restaurant', 'meal_takeaway', 'bar'];

                const res = await fetch('https://places.googleapis.com/v1/places:searchNearby', {
                    method: 'POST',
                    headers: {
                        'Content-Type': 'application/json',
                        'X-Goog-Api-Key': apiKey,
                        'X-Goog-FieldMask': 'places.id,places.displayName,places.formattedAddress,places.location,places.addressComponents,places.rating,places.primaryType,places.types'
                    },
                    body: JSON.stringify({
                        includedTypes,
                        maxResultCount: 20,
                        locationRestriction: {
                            circle: {
                                center: { latitude: lat, longitude: lng },
                                radius: 1800.0
                            }
                        }
                    })
                });

                if (res.ok) {
                    const data = await res.json();
                    if (Array.isArray(data.places) && data.places.length > 0) {
                        return data.places.map(p => {
                            const types = p.types || [];
                            let amenity = 'restaurant';
                            if (types.includes('cafe') || types.includes('coffee_shop') || types.includes('bakery') || category === 'cafe') {
                                amenity = 'cafe';
                            } else if (types.includes('fast_food_restaurant') || types.includes('bar') || types.includes('meal_takeaway') || category === 'fast_food') {
                                amenity = 'fast_food';
                            }
                            return {
                                id: p.id,
                                name: p.displayName?.text || 'Venue',
                                amenity,
                                cuisine: p.rating ? `★ ${p.rating}` : '',
                                lat: p.location?.latitude,
                                lng: p.location?.longitude,
                                address: p.addressComponents
                                    ? parseGoogleAddressComponents(p.addressComponents, p.formattedAddress)
                                    : { street: p.formattedAddress || '', city: '', state: '', zip: '', country: 'United Arab Emirates' }
                            };
                        }).filter(item => typeof item.lat === 'number' && typeof item.lng === 'number');
                    }
                }
            } catch (e) {
                console.warn('Google Places searchNearby fallback:', e);
            }
        }

        try {
            const url = `${config.API_BASE_URL}/api/v2/venues/osm-poi?lat=${lat}&lng=${lng}&category=${category}&radius=2500`;
            const res = await fetch(url, { headers: { Accept: 'application/json' } });
            if (res.ok) {
                const json = await res.json();
                if (json?.success && Array.isArray(json.data) && json.data.length > 0) return json.data;
            }
        } catch (_) {}

        return [];
    }, []);

    // Render POI markers
    const renderPoiMarkers = useCallback((poiList) => {
        poiMarkersRef.current.forEach(m => {
            if (m.setMap) m.setMap(null);
            else if (m.remove) m.remove();
        });
        poiMarkersRef.current = [];

        if (mapEngine === 'google' && googleMapRef.current && window.google?.maps) {
            if (!infoWindowRef.current) {
                infoWindowRef.current = new window.google.maps.InfoWindow();
            } else {
                infoWindowRef.current.close();
            }

            poiList.forEach(poi => {
                const marker = new window.google.maps.Marker({
                    position: { lat: poi.lat, lng: poi.lng },
                    map: googleMapRef.current,
                    icon: createGooglePoiIcon(poi.amenity),
                    title: poi.name
                });

                marker.addListener('click', () => {
                    if (!infoWindowRef.current || !googleMapRef.current) return;

                    let badgeColor = '#0369a1';
                    let badgeBg = '#e0f2fe';
                    if (poi.amenity === 'cafe') { badgeColor = '#b45309'; badgeBg = '#fef3c7'; }
                    else if (['fast_food', 'bar', 'pub'].includes(poi.amenity)) { badgeColor = '#6d28d9'; badgeBg = '#ede9fe'; }

                    const addrParts = [poi.address?.street, poi.address?.city, poi.address?.country].filter(Boolean);

                    infoWindowRef.current.setContent(`
                        <div style="font-family:-apple-system,BlinkMacSystemFont,'Segoe UI',Roboto,sans-serif;min-width:210px;max-width:255px;padding:4px;color:#0f172a;">
                            <div style="font-weight:700;font-size:14px;margin-bottom:5px;line-height:1.3;">${escapeHtml(poi.name)}</div>
                            <div style="display:flex;gap:5px;align-items:center;flex-wrap:wrap;margin-bottom:5px;">
                                <span style="font-size:10px;font-weight:700;padding:2px 8px;border-radius:999px;text-transform:uppercase;background:${badgeBg};color:${badgeColor};">
                                    ${escapeHtml(poi.amenity.replace('_', ' '))}
                                </span>
                                ${poi.cuisine ? `<span style="font-size:10px;font-weight:600;color:#d97706;background:#fffbeb;padding:2px 6px;border-radius:4px;">${escapeHtml(poi.cuisine)}</span>` : ''}
                            </div>
                            ${addrParts.length ? `<div style="font-size:11px;color:#64748b;margin-bottom:8px;line-height:1.4;">${escapeHtml(addrParts.join(', '))}</div>` : ''}
                            <button
                                id="hs-google-poi-select-btn"
                                style="width:100%;background:#0789d5;color:#fff;border:none;border-radius:6px;padding:7px 10px;font-size:12px;font-weight:600;cursor:pointer;">
                                ✓ Mark Venue Location Here
                            </button>
                        </div>
                    `);
                    infoWindowRef.current.open(googleMapRef.current, marker);

                    window.google.maps.event.addListenerOnce(infoWindowRef.current, 'domready', () => {
                        const btn = document.getElementById('hs-google-poi-select-btn');
                        if (btn) {
                            btn.onclick = async () => {
                                userInteractedRef.current = true;
                                setIsPinLocked(false);
                                isPinLockedRef.current = false;
                                if (googleMapRef.current) {
                                    googleMapRef.current.panTo({ lat: poi.lat, lng: poi.lng });
                                    googleMapRef.current.setZoom(18);
                                }
                                infoWindowRef.current.close();
                                const resolvedAddr = (poi.address?.city && poi.address?.street)
                                    ? poi.address
                                    : await reverseGeocodeLocation(poi.lat, poi.lng);
                                await updateLocation(poi.lat, poi.lng, resolvedAddr || poi.address, poi.name, true);
                                showFeedback('success', `Marked location at "${poi.name}"`);
                            };
                        }
                    });
                });

                poiMarkersRef.current.push(marker);
            });
        }
    }, [mapEngine, updateLocation, showFeedback]);

    // Load & render POIs
    const loadPois = useCallback(async (lat, lng, category) => {
        if (category === 'none') {
            if (isMountedRef.current) { setPois([]); renderPoiMarkers([]); }
            return;
        }
        if (isMountedRef.current) setIsPoiLoading(true);
        lastPoiFetchRef.current = { lat, lng };
        const results = await fetchPoiList(lat, lng, category);
        if (!isMountedRef.current) return;
        setPois(results);
        renderPoiMarkers(results);
        setIsPoiLoading(false);
    }, [fetchPoiList, renderPoiMarkers]);

    // Move map to target lat/lng and mark location
    const flyToAndMarkLocation = useCallback(async (lat, lng, customAddress = null, placeName = null, zoom = 17) => {
        userInteractedRef.current = true;
        setIsPinLocked(false);
        isPinLockedRef.current = false;

        if (mapEngine === 'google' && googleMapRef.current) {
            googleMapRef.current.panTo({ lat, lng });
            googleMapRef.current.setZoom(zoom);
        } else if (mapEngine === 'leaflet' && leafletMapRef.current) {
            leafletMapRef.current.setView([lat, lng], zoom, { animate: true });
        }

        await updateLocation(lat, lng, customAddress, placeName, true);
        if (poiCategoryRef.current !== 'none') {
            loadPois(lat, lng, poiCategoryRef.current);
        }
    }, [mapEngine, updateLocation, loadPois]);

    // Initialize Google Maps Engine
    useEffect(() => {
        if (mapEngine !== 'google' || !isApiLoaded || !window.google?.maps || googleMapRef.current || !mapContainerRef.current) {
            return;
        }

        try {
            const startCenter = { lat: initLat, lng: initLng };
            const startZoom = hasValidInitial ? 16 : DEFAULT_ZOOM;

            const map = new window.google.maps.Map(mapContainerRef.current, {
                center: startCenter,
                zoom: startZoom,
                mapTypeId: 'roadmap',
                mapTypeControl: false,
                streetViewControl: false,
                fullscreenControl: true,
                zoomControl: true,
                clickableIcons: true,
                gestureHandling: 'greedy',
                zoomControlOptions: {
                    position: window.google.maps.ControlPosition.RIGHT_BOTTOM
                }
            });

            googleMapRef.current = map;

            map.addListener('click', async (e) => {
                setSearchResults([]);
                if (infoWindowRef.current) {
                    infoWindowRef.current.close();
                }
                if (isPinLockedRef.current) {
                    showFeedback('info', 'Pin is locked. Click "Unlock Pin" to move location.');
                    return;
                }
                if (e.placeId) {
                    e.stop();
                    userInteractedRef.current = true;
                    const clickedLat = e.latLng ? e.latLng.lat() : null;
                    const clickedLng = e.latLng ? e.latLng.lng() : null;
                    const placeDetails = await fetchGooglePlaceById(e.placeId);
                    if (placeDetails) {
                        map.panTo({ lat: placeDetails.lat, lng: placeDetails.lng });
                        map.setZoom(18);
                        await updateLocation(placeDetails.lat, placeDetails.lng, placeDetails.address, placeDetails.name, true);
                        showFeedback('success', `Selected Google Maps Place: "${placeDetails.name}"`);
                        return;
                    } else if (clickedLat !== null && clickedLng !== null) {
                        map.panTo({ lat: clickedLat, lng: clickedLng });
                        await updateLocation(clickedLat, clickedLng, null, null, true);
                        return;
                    }
                }
                if (e.latLng) {
                    userInteractedRef.current = true;
                    map.panTo(e.latLng);
                }
            });

            map.addListener('dragstart', () => {
                if (!isPinLockedRef.current) {
                    userInteractedRef.current = true;
                }
            });

            map.addListener('idle', () => {
                if (idleTimerRef.current) clearTimeout(idleTimerRef.current);
                idleTimerRef.current = setTimeout(async () => {
                    if (!isMountedRef.current || !googleMapRef.current || isPinLockedRef.current) return;
                    const c = googleMapRef.current.getCenter();
                    if (!c) return;
                    const lat = c.lat();
                    const lng = c.lng();

                    // Map idle/panning should update map internal coordinates and geocoding label,
                    // but NEVER overwrite the parent form's address fields without explicit user interaction (click/select/drag)
                    await updateLocation(lat, lng, null, null, false);

                    const last = lastPoiFetchRef.current;
                    const dist = last ? Math.hypot(lat - last.lat, lng - last.lng) : 999;
                    if (dist > 0.005 && poiCategoryRef.current !== 'none') {
                        loadPois(lat, lng, poiCategoryRef.current);
                    }
                }, 350);
            });

            setIsLoading(false);

            // If no valid initial coordinates were stored (or it was stuck in the old desert default), geocode the venue's saved address or center on Dubai
            if (!hasValidInitial && initialAddress && (initialAddress.street || initialAddress.city)) {
                const addrQuery = [initialAddress.street, initialAddress.city, initialAddress.state, initialAddress.country || 'United Arab Emirates']
                    .filter(Boolean)
                    .join(', ');
                searchGooglePlacesNew(addrQuery).then((results) => {
                    const geo = results?.[0];
                    if (geo && isMountedRef.current && googleMapRef.current) {
                        googleMapRef.current.setCenter({ lat: geo.lat, lng: geo.lng });
                        googleMapRef.current.setZoom(16);
                        setLocation({ lat: geo.lat, lng: geo.lng });
                        setManualLat(geo.lat.toFixed(6));
                        setManualLng(geo.lng.toFixed(6));
                        if (geo.address) setGeocodingAddress(geo.address);
                        if (onLocationSelectRef.current && !hasInitialNotifiedRef.current) {
                            hasInitialNotifiedRef.current = true;
                            onLocationSelectRef.current({ lat: geo.lat, lng: geo.lng }, null, null);
                        }
                        if (poiCategoryRef.current !== 'none') {
                            loadPois(geo.lat, geo.lng, poiCategoryRef.current);
                        }
                    } else if (isMountedRef.current) {
                        loadPois(initLat, initLng, poiCategoryRef.current);
                    }
                });
            } else {
                // Initial load: don't overwrite address in parent
                updateLocation(initLat, initLng, null, null, false);
                if (poiCategoryRef.current !== 'none') {
                    setTimeout(() => {
                        if (isMountedRef.current) loadPois(initLat, initLng, poiCategoryRef.current);
                    }, 450);
                }
            }
        } catch (err) {
            console.error('Google Maps init error in MapPicker:', err);
            switchToLeaflet();
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [mapEngine, isApiLoaded]);

    // Initialize Leaflet fallback map if needed
    useEffect(() => {
        if (mapEngine !== 'leaflet' || leafletMapRef.current) return;

        loadLeaflet().then(L => {
            if (!isMountedRef.current || !mapContainerRef.current) return;
            leafletLibRef.current = L;

            delete L.Icon.Default.prototype._getIconUrl;
            L.Icon.Default.mergeOptions({
                iconUrl: 'https://unpkg.com/leaflet@1.9.4/dist/images/marker-icon.png',
                iconRetinaUrl: 'https://unpkg.com/leaflet@1.9.4/dist/images/marker-icon-2x.png',
                shadowUrl: 'https://unpkg.com/leaflet@1.9.4/dist/images/marker-shadow.png',
            });

            const map = L.map(mapContainerRef.current, {
                center: [location.lat, location.lng],
                zoom: hasValidInitial ? 16 : DEFAULT_ZOOM,
                zoomControl: false,
                attributionControl: true,
            });

            L.control.zoom({ position: 'bottomright' }).addTo(map);
            L.tileLayer('https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png', {
                attribution: '&copy; OpenStreetMap contributors',
                maxZoom: 19,
            }).addTo(map);

            leafletMapRef.current = map;
            setIsLoading(false);
        }).catch(() => {
            setIsLoading(false);
        });
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [mapEngine]);

    // Sync map center if initialLocation prop updates dynamically
    useEffect(() => {
        if (!hasValidInitial || userInteractedRef.current) return;
        const { lat, lng } = initialLocation;
        setLocation({ lat, lng });
        setManualLat(lat.toFixed(6));
        setManualLng(lng.toFixed(6));
        if (mapEngine === 'google' && googleMapRef.current) {
            googleMapRef.current.setCenter({ lat, lng });
            googleMapRef.current.setZoom(16);
        } else if (mapEngine === 'leaflet' && leafletMapRef.current) {
            leafletMapRef.current.setView([lat, lng], 16);
        }
    }, [initialLocation, hasValidInitial, mapEngine]);

    // Toggle Google Maps Roadmap / Satellite Hybrid
    const handleToggleMapType = useCallback((type) => {
        setMapTypeId(type);
        if (googleMapRef.current) {
            googleMapRef.current.setMapTypeId(type);
        }
    }, []);

    // Resolve any pasted Google Maps link / Plus Code / Coordinate string
    const handleResolveSharedLink = useCallback(async (inputStr = sharedLinkInput) => {
        const raw = (inputStr || '').trim();
        if (!raw) {
            showFeedback('danger', 'Please paste a Google Maps link, Plus Code, or coordinates first.');
            return;
        }

        setIsResolvingLink(true);
        setToolStatusMsg(null);

        try {
            const parsed = parseMapInputString(raw);

            if (parsed?.type === 'coords') {
                await flyToAndMarkLocation(parsed.lat, parsed.lng, null, parsed.placeName || null, 18);
                showFeedback('success', `Location marked from link (${parsed.lat.toFixed(5)}, ${parsed.lng.toFixed(5)})`);
                setIsResolvingLink(false);
                setOpenDrawer('none');
                return;
            }

            if (parsed?.type === 'short_url') {
                try {
                    const res = await fetch(`${config.API_BASE_URL}/api/v2/venues/resolve-map-link?url=${encodeURIComponent(parsed.url)}`);
                    if (res.ok) {
                        const data = await res.json();
                        if (data.success && typeof data.lat === 'number' && typeof data.lng === 'number') {
                            await flyToAndMarkLocation(data.lat, data.lng, null, data.placeName || null, 18);
                            showFeedback('success', `Resolved Google Maps link${data.placeName ? `: "${data.placeName}"` : ''}!`);
                            setIsResolvingLink(false);
                            setOpenDrawer('none');
                            return;
                        }
                        if (data.success && data.placeName) {
                            const places = await searchGooglePlacesNew(data.placeName, location);
                            if (places.length > 0) {
                                const top = places[0];
                                await flyToAndMarkLocation(top.lat, top.lng, top.address, top.name, 18);
                                showFeedback('success', `Resolved link to "${top.name}"!`);
                                setIsResolvingLink(false);
                                setOpenDrawer('none');
                                return;
                            }
                        }
                    }
                } catch (err) {
                    console.warn('Backend resolve-map-link fallback:', err);
                }
            }

            const queryToSearch = parsed?.query || raw;
            const places = await searchGooglePlacesNew(queryToSearch, location);
            if (places.length > 0) {
                const top = places[0];
                await flyToAndMarkLocation(top.lat, top.lng, top.address, top.name, 18);
                showFeedback('success', `Marked location: "${top.name}" (${top.lat.toFixed(5)}, ${top.lng.toFixed(5)})`);
                setOpenDrawer('none');
            } else {
                showFeedback('danger', 'Could not extract coordinates from this link. Try copying the full URL from your browser address bar or searching the venue name.');
            }
        } catch (err) {
            showFeedback('danger', 'Failed to resolve link. Try searching by place name or entering coordinates.');
        } finally {
            if (isMountedRef.current) setIsResolvingLink(false);
        }
    }, [sharedLinkInput, location, flyToAndMarkLocation, showFeedback]);

    // 1-Click Paste Link from Clipboard
    const handlePasteFromClipboard = useCallback(async () => {
        try {
            if (navigator.clipboard?.readText) {
                const text = await navigator.clipboard.readText();
                if (text && text.trim()) {
                    setSharedLinkInput(text.trim());
                    setOmniboxValue(text.trim());
                    await handleResolveSharedLink(text.trim());
                    return;
                }
            }
            setOpenDrawer(prev => (prev === 'link' ? 'none' : 'link'));
        } catch (_) {
            setOpenDrawer('link');
        }
    }, [handleResolveSharedLink]);

    // Execute Search / Omnibox Action
    const executeOmniboxSearch = useCallback(async (queryText) => {
        const q = (queryText ?? omniboxValue).trim();
        if (!q) {
            setSearchResults([]);
            return;
        }

        // Automatically detect if the user pasted a Google Maps link or raw coordinates into the main search bar
        const parsedLink = parseMapInputString(q);
        if (parsedLink) {
            setSharedLinkInput(q);
            await handleResolveSharedLink(q);
            setSearchResults([]);
            return;
        }

        setIsSearching(true);
        let foundResults = await searchGooglePlacesNew(q, location);

        if (foundResults.length === 0) {
            try {
                const url = `https://nominatim.openstreetmap.org/search?format=json&q=${encodeURIComponent(q)}&addressdetails=1&limit=6&accept-language=en`;
                const res = await fetch(url, { headers: { Accept: 'application/json' } });
                if (res.ok) {
                    const data = await res.json();
                    if (Array.isArray(data)) {
                        foundResults = data.map(item => ({
                            name: item.name || item.display_name?.split(',')[0] || 'Location',
                            formatted_address: item.display_name,
                            lat: parseFloat(item.lat),
                            lng: parseFloat(item.lon),
                        }));
                    }
                }
            } catch (_) {}
        }

        if (isMountedRef.current) {
            setSearchResults(foundResults);
            setIsSearching(false);
        }
    }, [omniboxValue, location, handleResolveSharedLink]);

    const handleOmniboxChange = (e) => {
        const val = e.target.value;
        setOmniboxValue(val);

        if (searchDebounceRef.current) clearTimeout(searchDebounceRef.current);
        if (!val.trim() || val.trim().length < 2) {
            setSearchResults([]);
            return;
        }

        // If it looks like a URL or coordinate pair, resolve on paste/Enter rather than autocomplete
        if (/^https?:\/\//i.test(val.trim())) {
            return;
        }

        searchDebounceRef.current = setTimeout(() => {
            executeOmniboxSearch(val);
        }, 300);
    };

    const handleResultSelect = useCallback(async (result) => {
        setSearchResults([]);
        setOmniboxValue(result.name || '');
        await flyToAndMarkLocation(result.lat, result.lng, result.address || null, result.name, 18);
        showFeedback('success', `Marked location at "${result.name}"`);
    }, [flyToAndMarkLocation, showFeedback]);

    const handleApplyManualCoords = useCallback(async () => {
        const lat = parseFloat(manualLat);
        const lng = parseFloat(manualLng);
        if (isNaN(lat) || isNaN(lng) || lat < -90 || lat > 90 || lng < -180 || lng > 180) {
            showFeedback('danger', 'Enter valid Latitude (-90 to 90) and Longitude (-180 to 180).');
            return;
        }
        await flyToAndMarkLocation(lat, lng, null, null, 17);
        showFeedback('success', `Moved pin to ${lat.toFixed(6)}, ${lng.toFixed(6)}`);
        setOpenDrawer('none');
    }, [manualLat, manualLng, flyToAndMarkLocation, showFeedback]);

    const handleNudgePin = useCallback(async (dLat, dLng) => {
        const newLat = location.lat + dLat;
        const newLng = location.lng + dLng;
        await flyToAndMarkLocation(newLat, newLng, null, null, googleMapRef.current?.getZoom() || 18);
    }, [location, flyToAndMarkLocation]);

    const handlePoiCategoryChange = useCallback((category) => {
        setPoiCategory(category);
        if (mapEngine === 'google' && googleMapRef.current) {
            const c = googleMapRef.current.getCenter();
            if (c) loadPois(c.lat(), c.lng(), category);
        } else if (mapEngine === 'leaflet' && leafletMapRef.current) {
            const c = leafletMapRef.current.getCenter();
            loadPois(c.lat, c.lng, category);
        }
    }, [loadPois, mapEngine]);

    const getCurrentLocation = useCallback(() => {
        if (!navigator.geolocation) {
            showFeedback('danger', 'Geolocation is not supported by your browser.');
            return;
        }
        userInteractedRef.current = true;
        setIsLoading(true);
        navigator.geolocation.getCurrentPosition(
            async (pos) => {
                const lat = pos.coords.latitude;
                const lng = pos.coords.longitude;
                await flyToAndMarkLocation(lat, lng, null, null, 17);
                setIsLoading(false);
                showFeedback('success', 'Pinned your current GPS location!');
            },
            () => {
                showFeedback('danger', 'Unable to access GPS. Please allow browser location permission.');
                setIsLoading(false);
            },
            { enableHighAccuracy: true, timeout: 10000, maximumAge: 0 }
        );
    }, [flyToAndMarkLocation, showFeedback]);

    const handleCopyShareLink = useCallback(async () => {
        const shareUrl = `https://www.google.com/maps?q=${location.lat.toFixed(6)},${location.lng.toFixed(6)}`;
        try {
            await navigator.clipboard.writeText(shareUrl);
            showFeedback('success', 'Copied Google Maps link to clipboard!');
        } catch (_) {
            showFeedback('info', shareUrl);
        }
    }, [location, showFeedback]);

    const handleSyncAddressNow = useCallback(async () => {
        userInteractedRef.current = true;
        const addr = await reverseGeocodeLocation(location.lat, location.lng);
        if (addr) {
            setGeocodingAddress(addr);
            if (onLocationSelectRef.current) {
                onLocationSelectRef.current(location, addr, selectedPlaceName || null);
            }
            showFeedback('success', 'Address fields filled from current map pin!');
        }
    }, [location, selectedPlaceName, showFeedback]);

    return (
        <div
            id="hs-pro-map-picker"
            style={{
                height,
                display: 'flex',
                flexDirection: 'column',
                background: '#ffffff',
                borderRadius: '12px',
                overflow: 'hidden',
                fontFamily: "-apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, sans-serif"
            }}
        >
            <style>{`
                #hs-pro-map-picker button,
                #hs-pro-map-picker a {
                    aspect-ratio: unset !important;
                    min-width: 0 !important;
                    min-height: 0 !important;
                    box-shadow: none;
                }
                #hs-pro-map-picker button:hover {
                    filter: brightness(0.97);
                }
                #hs-pro-map-picker input {
                    min-height: 0 !important;
                    box-shadow: none !important;
                }
            `}</style>
            {/* ===================================================================
                ROW 1: UNIFIED SMART OMNIBOX (Search + Paste Link + GPS)
               =================================================================== */}
            <div
                style={{
                    padding: '12px 14px',
                    background: '#ffffff',
                    borderBottom: '1px solid #e2e8f0',
                    display: 'flex',
                    alignItems: 'center',
                    gap: '8px',
                    flexWrap: 'wrap',
                    position: 'relative',
                    zIndex: 1050
                }}
            >
                {/* Omnibox Input Wrapper */}
                <div ref={omniboxWrapperRef} style={{ flex: '1 1 280px', position: 'relative' }}>
                    <div
                        style={{
                            display: 'flex',
                            alignItems: 'center',
                            background: '#f8fafc',
                            border: '1px solid #cbd5e1',
                            borderRadius: '10px',
                            height: '40px',
                            padding: '0 10px',
                            transition: 'border-color 0.15s'
                        }}
                    >
                        <Icon
                            icon="logos:google-maps"
                            style={{ fontSize: '18px', flexShrink: 0, marginRight: '8px' }}
                        />
                        <input
                            type="text"
                            placeholder={matchedCityKey ? `Search in ${matchedCityKey} or paste Maps link...` : "Search restaurant, cafe, street on Google Maps — or paste a shared Maps link..."}
                            value={omniboxValue}
                            onFocus={() => setIsOmniboxFocused(true)}
                            onChange={handleOmniboxChange}
                            onPaste={e => {
                                const pasted = e.clipboardData?.getData('text');
                                if (pasted && parseMapInputString(pasted.trim())) {
                                    setTimeout(() => handleResolveSharedLink(pasted.trim()), 40);
                                }
                            }}
                            onKeyDown={e => {
                                if (e.key === 'Enter') {
                                    e.preventDefault();
                                    executeOmniboxSearch();
                                }
                            }}
                            style={{
                                border: 'none',
                                outline: 'none',
                                background: 'transparent',
                                width: '100%',
                                fontSize: '13px',
                                color: '#0f172a',
                                fontWeight: 500
                            }}
                        />
                        {omniboxValue && (
                            <button
                                type="button"
                                onClick={() => { setOmniboxValue(''); setSearchResults([]); }}
                                style={{
                                    border: 'none',
                                    background: 'transparent',
                                    color: '#94a3b8',
                                    padding: '4px',
                                    cursor: 'pointer',
                                    display: 'flex',
                                    alignItems: 'center'
                                }}
                                title="Clear"
                            >
                                <Icon icon="solar:close-circle-bold" style={{ fontSize: '16px' }} />
                            </button>
                        )}
                    </div>

                    {/* Live Google Maps Autocomplete Dropdown */}
                    {searchResults.length > 0 && (
                        <div
                            style={{
                                position: 'absolute',
                                top: '44px',
                                left: 0,
                                right: 0,
                                background: '#ffffff',
                                border: '1px solid #e2e8f0',
                                borderRadius: '10px',
                                boxShadow: '0 12px 28px -6px rgba(15, 23, 42, 0.18)',
                                maxHeight: '280px',
                                overflowY: 'auto',
                                zIndex: 1200
                            }}
                        >
                            {searchResults.map((r, i) => (
                                <div
                                    key={r.id || i}
                                    onClick={() => handleResultSelect(r)}
                                    style={{
                                        padding: '10px 14px',
                                        borderBottom: i < searchResults.length - 1 ? '1px solid #f1f5f9' : 'none',
                                        display: 'flex',
                                        alignItems: 'center',
                                        justifyContent: 'space-between',
                                        gap: '10px',
                                        cursor: 'pointer'
                                    }}
                                    onMouseEnter={e => e.currentTarget.style.background = '#f0f9ff'}
                                    onMouseLeave={e => e.currentTarget.style.background = '#ffffff'}
                                >
                                    <div style={{ display: 'flex', alignItems: 'flex-start', gap: '10px', minWidth: 0 }}>
                                        <Icon
                                            icon="solar:map-point-bold-duotone"
                                            style={{ fontSize: '20px', color: '#0789d5', marginTop: '2px', flexShrink: 0 }}
                                        />
                                        <div style={{ minWidth: 0 }}>
                                            <div style={{ display: 'flex', alignItems: 'center', gap: '6px', flexWrap: 'wrap' }}>
                                                <span style={{ fontWeight: 600, fontSize: '13px', color: '#0f172a' }}>{r.name}</span>
                                                {r.rating && (
                                                    <span
                                                        style={{
                                                            fontSize: '10px',
                                                            fontWeight: 700,
                                                            background: '#fef3c7',
                                                            color: '#b45309',
                                                            padding: '2px 6px',
                                                            borderRadius: '4px'
                                                        }}
                                                    >
                                                        ★ {r.rating}
                                                    </span>
                                                )}
                                                {r.category && (
                                                    <span
                                                        style={{
                                                            fontSize: '10px',
                                                            fontWeight: 600,
                                                            background: '#e0f2fe',
                                                            color: '#0369a1',
                                                            padding: '2px 6px',
                                                            borderRadius: '4px',
                                                            textTransform: 'capitalize'
                                                        }}
                                                    >
                                                        {r.category}
                                                    </span>
                                                )}
                                            </div>
                                            <div
                                                style={{
                                                    fontSize: '11px',
                                                    color: '#64748b',
                                                    whiteSpace: 'nowrap',
                                                    overflow: 'hidden',
                                                    textOverflow: 'ellipsis'
                                                }}
                                            >
                                                {r.formatted_address}
                                            </div>
                                        </div>
                                    </div>
                                    <span
                                        style={{
                                            fontSize: '11px',
                                            fontWeight: 600,
                                            color: '#0789d5',
                                            background: '#eff6ff',
                                            padding: '4px 10px',
                                            borderRadius: '6px',
                                            flexShrink: 0
                                        }}
                                    >
                                        Select
                                    </span>
                                </div>
                            ))}
                        </div>
                    )}

                    {/* City Default Suggestions Dropdown (Shown when focused and no active search results) */}
                    {searchResults.length === 0 && isOmniboxFocused && cityDefaultSuggestions.length > 0 && !omniboxValue.trim() && (
                        <div
                            style={{
                                position: 'absolute',
                                top: '44px',
                                left: 0,
                                right: 0,
                                background: '#ffffff',
                                border: '1px solid #cbd5e1',
                                borderRadius: '10px',
                                boxShadow: '0 12px 28px -6px rgba(15, 23, 42, 0.18)',
                                maxHeight: '310px',
                                overflowY: 'auto',
                                zIndex: 1200
                            }}
                        >
                            <div
                                style={{
                                    padding: '8px 14px',
                                    background: '#f8fafc',
                                    borderBottom: '1px solid #e2e8f0',
                                    display: 'flex',
                                    alignItems: 'center',
                                    justifyContent: 'space-between'
                                }}
                            >
                                <span style={{ fontSize: '11px', fontWeight: 700, color: '#0369a1', textTransform: 'uppercase', letterSpacing: '0.5px' }}>
                                    Popular Streets & Locations in {matchedCityKey}
                                </span>
                                <span style={{ fontSize: '10px', color: '#64748b' }}>
                                    Click to quick-jump map & pin
                                </span>
                            </div>
                            {cityDefaultSuggestions.map((item, idx) => (
                                <div
                                    key={idx}
                                    onClick={() => {
                                        setIsOmniboxFocused(false);
                                        setOmniboxValue(item.name);
                                        flyToAndMarkLocation(
                                            item.lat,
                                            item.lng,
                                            {
                                                street: item.street,
                                                city: matchedCityKey,
                                                state: matchedCityKey,
                                                zip: '',
                                                country: 'United Arab Emirates'
                                            },
                                            item.name,
                                            17
                                        );
                                        showFeedback('success', `Marked location: "${item.name}"`);
                                    }}
                                    style={{
                                        padding: '10px 14px',
                                        borderBottom: idx < cityDefaultSuggestions.length - 1 ? '1px solid #f1f5f9' : 'none',
                                        display: 'flex',
                                        alignItems: 'center',
                                        justifyContent: 'space-between',
                                        gap: '10px',
                                        cursor: 'pointer'
                                    }}
                                    onMouseEnter={e => e.currentTarget.style.background = '#f0f9ff'}
                                    onMouseLeave={e => e.currentTarget.style.background = '#ffffff'}
                                >
                                    <div style={{ display: 'flex', alignItems: 'flex-start', gap: '10px', minWidth: 0 }}>
                                        <Icon
                                            icon="solar:streets-map-point-bold-duotone"
                                            style={{ fontSize: '18px', color: '#0789d5', marginTop: '2px', flexShrink: 0 }}
                                        />
                                        <div style={{ minWidth: 0 }}>
                                            <div style={{ display: 'flex', alignItems: 'center', gap: '6px' }}>
                                                <span style={{ fontWeight: 600, fontSize: '13px', color: '#0f172a' }}>{item.name}</span>
                                                {item.area && (
                                                    <span
                                                        style={{
                                                            fontSize: '10px',
                                                            fontWeight: 600,
                                                            background: '#eff6ff',
                                                            color: '#0284c7',
                                                            padding: '1px 6px',
                                                            borderRadius: '4px'
                                                        }}
                                                    >
                                                        {item.area}
                                                    </span>
                                                )}
                                            </div>
                                            <div
                                                style={{
                                                    fontSize: '11px',
                                                    color: '#64748b',
                                                    whiteSpace: 'nowrap',
                                                    overflow: 'hidden',
                                                    textOverflow: 'ellipsis'
                                                }}
                                            >
                                                {item.street}
                                            </div>
                                        </div>
                                    </div>
                                    <span
                                        style={{
                                            fontSize: '11px',
                                            fontWeight: 600,
                                            color: '#0789d5',
                                            background: '#e0f2fe',
                                            padding: '4px 9px',
                                            borderRadius: '6px',
                                            flexShrink: 0
                                        }}
                                    >
                                        Use
                                    </span>
                                </div>
                            ))}
                        </div>
                    )}
                </div>

                {/* Primary Search Button */}
                <button
                    type="button"
                    onClick={() => executeOmniboxSearch()}
                    disabled={isSearching || isResolvingLink}
                    style={{
                        ...btnBase,
                        height: '40px',
                        padding: '0 16px',
                        background: '#0789d5',
                        color: '#ffffff',
                        border: '1px solid #0789d5'
                    }}
                >
                    {isSearching || isResolvingLink ? (
                        <span className="spinner-border spinner-border-sm" style={{ width: 14, height: 14 }} />
                    ) : (
                        <Icon icon="solar:magnifer-linear" style={{ fontSize: '15px' }} />
                    )}
                    <span>Search</span>
                </button>

                {/* 1-Click Paste Maps Link Button */}
                <button
                    type="button"
                    onClick={handlePasteFromClipboard}
                    style={{
                        ...btnBase,
                        height: '40px',
                        padding: '0 14px',
                        background: openDrawer === 'link' ? '#eff6ff' : '#ffffff',
                        color: '#0789d5',
                        border: '1px solid #bae6fd'
                    }}
                    title="Paste shared Google Maps link from clipboard or open link input"
                >
                    <Icon icon="solar:link-circle-bold" style={{ fontSize: '16px' }} />
                    <span>Paste Maps Link</span>
                </button>

                {/* 1-Click My GPS Location Button */}
                <button
                    type="button"
                    onClick={getCurrentLocation}
                    style={{
                        ...btnBase,
                        height: '40px',
                        padding: '0 14px',
                        background: '#ffffff',
                        color: '#0f172a',
                        border: '1px solid #cbd5e1'
                    }}
                    title="Use my current GPS location"
                >
                    <Icon icon="solar:gps-bold" style={{ fontSize: '16px', color: '#0789d5' }} />
                    <span>My GPS</span>
                </button>
            </div>

            {/* ===================================================================
                COLLAPSIBLE DRAWER: PASTE LINK OR MANUAL COORDINATES (When Toggled)
               =================================================================== */}
            {openDrawer === 'link' && (
                <div
                    style={{
                        padding: '10px 14px',
                        background: '#f0f9ff',
                        borderBottom: '1px solid #bae6fd',
                        display: 'flex',
                        alignItems: 'center',
                        gap: '8px',
                        flexWrap: 'wrap'
                    }}
                >
                    <Icon icon="solar:link-bold" style={{ fontSize: '16px', color: '#0284c7' }} />
                    <input
                        type="text"
                        placeholder="Paste shared Google Maps URL (https://maps.app.goo.gl/... or google.com/maps/...), Plus Code, or Lat, Lng..."
                        value={sharedLinkInput}
                        onChange={e => setSharedLinkInput(e.target.value)}
                        onPaste={e => {
                            const pasted = e.clipboardData?.getData('text');
                            if (pasted && pasted.trim()) {
                                setTimeout(() => handleResolveSharedLink(pasted.trim()), 40);
                            }
                        }}
                        onKeyDown={e => {
                            if (e.key === 'Enter') {
                                e.preventDefault();
                                handleResolveSharedLink();
                            }
                        }}
                        style={{
                            flex: '1 1 240px',
                            height: '34px',
                            padding: '0 12px',
                            borderRadius: '8px',
                            border: '1px solid #7dd3fc',
                            background: '#ffffff',
                            fontSize: '12px',
                            outline: 'none'
                        }}
                    />
                    <button
                        type="button"
                        onClick={() => handleResolveSharedLink()}
                        disabled={isResolvingLink}
                        style={{
                            ...btnBase,
                            height: '34px',
                            padding: '0 14px',
                            background: '#0284c7',
                            color: '#ffffff',
                            border: 'none'
                        }}
                    >
                        <Icon icon="solar:map-point-wave-bold" style={{ fontSize: '14px' }} />
                        <span>{isResolvingLink ? 'Locating...' : 'Locate Pin'}</span>
                    </button>
                    <button
                        type="button"
                        onClick={() => setOpenDrawer('none')}
                        style={{
                            ...btnBase,
                            height: '34px',
                            padding: '0 10px',
                            background: 'transparent',
                            color: '#64748b',
                            border: 'none'
                        }}
                    >
                        Cancel
                    </button>
                </div>
            )}

            {openDrawer === 'coords' && (
                <div
                    style={{
                        padding: '10px 14px',
                        background: '#f8fafc',
                        borderBottom: '1px solid #e2e8f0',
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'space-between',
                        gap: '10px',
                        flexWrap: 'wrap'
                    }}
                >
                    <div style={{ display: 'flex', alignItems: 'center', gap: '8px', flexWrap: 'wrap' }}>
                        <span style={{ fontSize: '12px', fontWeight: 600, color: '#475569' }}>Coordinates:</span>
                        <div style={{ display: 'flex', alignItems: 'center', gap: '4px' }}>
                            <span style={{ fontSize: '11px', color: '#64748b' }}>Lat</span>
                            <input
                                type="number"
                                step="any"
                                value={manualLat}
                                onChange={e => setManualLat(e.target.value)}
                                style={{
                                    width: '115px',
                                    height: '32px',
                                    padding: '0 8px',
                                    borderRadius: '6px',
                                    border: '1px solid #cbd5e1',
                                    fontSize: '12px'
                                }}
                            />
                        </div>
                        <div style={{ display: 'flex', alignItems: 'center', gap: '4px' }}>
                            <span style={{ fontSize: '11px', color: '#64748b' }}>Lng</span>
                            <input
                                type="number"
                                step="any"
                                value={manualLng}
                                onChange={e => setManualLng(e.target.value)}
                                style={{
                                    width: '115px',
                                    height: '32px',
                                    padding: '0 8px',
                                    borderRadius: '6px',
                                    border: '1px solid #cbd5e1',
                                    fontSize: '12px'
                                }}
                            />
                        </div>
                        <button
                            type="button"
                            onClick={handleApplyManualCoords}
                            style={{
                                ...btnBase,
                                height: '32px',
                                padding: '0 12px',
                                background: '#0789d5',
                                color: '#ffffff',
                                border: 'none'
                            }}
                        >
                            Go to Pin
                        </button>
                    </div>

                    <div style={{ display: 'flex', alignItems: 'center', gap: '4px' }}>
                        <span style={{ fontSize: '11px', color: '#64748b', marginRight: '4px' }}>Micro-Adjust:</span>
                        {[
                            { label: '↑', dLat: 0.00005, dLng: 0, title: 'Nudge North' },
                            { label: '↓', dLat: -0.00005, dLng: 0, title: 'Nudge South' },
                            { label: '←', dLat: 0, dLng: -0.00005, title: 'Nudge West' },
                            { label: '→', dLat: 0, dLng: 0.00005, title: 'Nudge East' },
                        ].map((n, idx) => (
                            <button
                                key={idx}
                                type="button"
                                onClick={() => handleNudgePin(n.dLat, n.dLng)}
                                title={n.title}
                                style={{
                                    ...btnBase,
                                    width: '28px',
                                    height: '28px',
                                    padding: 0,
                                    background: '#ffffff',
                                    border: '1px solid #cbd5e1',
                                    color: '#334155'
                                }}
                            >
                                {n.label}
                            </button>
                        ))}
                    </div>
                </div>
            )}

            {/* ===================================================================
                ROW 2: COMPACT PRO TOOLBAR (UAE Cities | Nearby Filter | View Controls)
               =================================================================== */}
            <div
                style={{
                    padding: '8px 14px',
                    background: '#f8fafc',
                    borderBottom: '1px solid #e2e8f0',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'space-between',
                    gap: '10px',
                    flexWrap: 'wrap'
                }}
            >
                {/* Left: Quick UAE City Jump Pills */}
                <div style={{ display: 'flex', alignItems: 'center', gap: '4px', flexWrap: 'wrap' }}>
                    <span style={{ fontSize: '11px', fontWeight: 700, color: '#64748b', textTransform: 'uppercase', marginRight: '4px' }}>
                        UAE:
                    </span>
                    {UAE_CITIES.map(city => (
                        <button
                            key={city.name}
                            type="button"
                            onClick={() => flyToAndMarkLocation(city.lat, city.lng, null, null, city.zoom)}
                            style={{
                                ...btnBase,
                                height: '26px',
                                padding: '0 9px',
                                fontSize: '11px',
                                fontWeight: 600,
                                borderRadius: '6px',
                                background: '#ffffff',
                                color: '#334155',
                                border: '1px solid #e2e8f0'
                            }}
                        >
                            {city.name}
                        </button>
                    ))}
                </div>

                {/* Right: Nearby Venues Filter + Coordinates Toggle + Map/Satellite + Lock Pin */}
                <div style={{ display: 'flex', alignItems: 'center', gap: '6px', flexWrap: 'wrap' }}>
                    {/* Explore Nearby Filter */}
                    <div
                        style={{
                            display: 'inline-flex',
                            alignItems: 'center',
                            background: '#ffffff',
                            border: '1px solid #e2e8f0',
                            borderRadius: '7px',
                            padding: '2px'
                        }}
                    >
                        {[
                            { key: 'all',        label: 'All Nearby' },
                            { key: 'restaurant', label: 'Restaurants' },
                            { key: 'cafe',       label: 'Cafes' },
                            { key: 'none',       label: 'Hide Pins' },
                        ].map(cat => {
                            const active = poiCategory === cat.key;
                            return (
                                <button
                                    key={cat.key}
                                    type="button"
                                    onClick={() => handlePoiCategoryChange(cat.key)}
                                    style={{
                                        ...btnBase,
                                        height: '24px',
                                        padding: '0 8px',
                                        fontSize: '11px',
                                        borderRadius: '5px',
                                        border: 'none',
                                        background: active ? '#0789d5' : 'transparent',
                                        color: active ? '#ffffff' : '#64748b'
                                    }}
                                >
                                    {cat.label}
                                </button>
                            );
                        })}
                    </div>

                    {/* Manual Lat/Lng Drawer Toggle */}
                    <button
                        type="button"
                        onClick={() => setOpenDrawer(prev => (prev === 'coords' ? 'none' : 'coords'))}
                        style={{
                            ...btnBase,
                            height: '28px',
                            padding: '0 10px',
                            fontSize: '11px',
                            background: openDrawer === 'coords' ? '#eff6ff' : '#ffffff',
                            color: openDrawer === 'coords' ? '#0789d5' : '#475569',
                            border: '1px solid #e2e8f0'
                        }}
                        title="Enter exact Latitude & Longitude or fine-nudge pin"
                    >
                        <Icon icon="solar:compass-bold" style={{ fontSize: '14px' }} />
                        <span>Lat/Lng</span>
                    </button>

                    {/* Map / Satellite Toggle */}
                    {mapEngine === 'google' && (
                        <div
                            style={{
                                display: 'inline-flex',
                                alignItems: 'center',
                                background: '#ffffff',
                                border: '1px solid #e2e8f0',
                                borderRadius: '7px',
                                padding: '2px'
                            }}
                        >
                            <button
                                type="button"
                                onClick={() => handleToggleMapType('roadmap')}
                                style={{
                                    ...btnBase,
                                    height: '24px',
                                    padding: '0 8px',
                                    fontSize: '11px',
                                    borderRadius: '5px',
                                    border: 'none',
                                    background: mapTypeId === 'roadmap' ? '#0f172a' : 'transparent',
                                    color: mapTypeId === 'roadmap' ? '#ffffff' : '#64748b'
                                }}
                            >
                                Map
                            </button>
                            <button
                                type="button"
                                onClick={() => handleToggleMapType('hybrid')}
                                style={{
                                    ...btnBase,
                                    height: '24px',
                                    padding: '0 8px',
                                    fontSize: '11px',
                                    borderRadius: '5px',
                                    border: 'none',
                                    background: mapTypeId === 'hybrid' ? '#0f172a' : 'transparent',
                                    color: mapTypeId === 'hybrid' ? '#ffffff' : '#64748b'
                                }}
                            >
                                Satellite
                            </button>
                        </div>
                    )}

                    {/* Lock Pin Button */}
                    <button
                        type="button"
                        onClick={() => setIsPinLocked(prev => !prev)}
                        style={{
                            ...btnBase,
                            height: '28px',
                            padding: '0 10px',
                            fontSize: '11px',
                            background: isPinLocked ? '#fef3c7' : '#ffffff',
                            color: isPinLocked ? '#b45309' : '#475569',
                            border: isPinLocked ? '1px solid #fcd34d' : '1px solid #e2e8f0'
                        }}
                        title={isPinLocked ? 'Unlock pin to move location' : 'Lock pin position'}
                    >
                        <Icon
                            icon={isPinLocked ? 'solar:lock-keyhole-bold' : 'solar:lock-keyhole-minimalistic-unlocked-bold'}
                            style={{ fontSize: '14px' }}
                        />
                        <span>{isPinLocked ? 'Locked' : 'Lock Pin'}</span>
                    </button>
                </div>
            </div>

            {/* Feedback Banner (if active) */}
            {toolStatusMsg && (
                <div
                    style={{
                        padding: '6px 14px',
                        background: toolStatusMsg.type === 'success' ? '#ecfdf5' : toolStatusMsg.type === 'danger' ? '#fef2f2' : '#f0f9ff',
                        color: toolStatusMsg.type === 'success' ? '#047857' : toolStatusMsg.type === 'danger' ? '#b91c1c' : '#0369a1',
                        borderBottom: '1px solid #e2e8f0',
                        fontSize: '12px',
                        fontWeight: 600,
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'space-between'
                    }}
                >
                    <div style={{ display: 'flex', alignItems: 'center', gap: '6px' }}>
                        <Icon
                            icon={toolStatusMsg.type === 'success' ? 'solar:check-circle-bold' : 'solar:info-circle-bold'}
                            style={{ fontSize: '15px' }}
                        />
                        <span>{toolStatusMsg.text}</span>
                    </div>
                    <button
                        type="button"
                        onClick={() => setToolStatusMsg(null)}
                        style={{ border: 'none', background: 'transparent', color: 'inherit', cursor: 'pointer', fontSize: '14px' }}
                    >
                        ✕
                    </button>
                </div>
            )}

            {/* ===================================================================
                INTERACTIVE GOOGLE MAP CANVAS (Unobstructed!)
               =================================================================== */}
            <div style={{ position: 'relative', flex: '1 1 auto', minHeight: '360px' }}>
                <div ref={mapContainerRef} style={{ height: '100%', width: '100%' }} />

                {/* Subtle Nearby Status Pill in Top-Left of Map */}
                {poiCategory !== 'none' && (
                    <div
                        style={{
                            position: 'absolute',
                            top: '10px',
                            left: '10px',
                            zIndex: 800,
                            background: 'rgba(255, 255, 255, 0.94)',
                            backdropFilter: 'blur(4px)',
                            padding: '4px 10px',
                            borderRadius: '999px',
                            border: '1px solid #e2e8f0',
                            boxShadow: '0 2px 6px rgba(0,0,0,0.06)',
                            display: 'inline-flex',
                            alignItems: 'center',
                            gap: '6px',
                            fontSize: '11px',
                            fontWeight: 600,
                            color: '#475569'
                        }}
                    >
                        {isPoiLoading ? (
                            <>
                                <span className="spinner-border spinner-border-sm text-primary" style={{ width: 10, height: 10, borderWidth: 2 }} />
                                <span>Finding nearby places...</span>
                            </>
                        ) : (
                            <>
                                <span style={{ width: 7, height: 7, borderRadius: '50%', background: '#16a34a', display: 'inline-block' }} />
                                <span>{pois.length} places nearby — click any icon on map to select</span>
                            </>
                        )}
                    </div>
                )}

                {/* Centre Target Crosshair Pin */}
                <div
                    style={{
                        position: 'absolute',
                        top: '50%',
                        left: '50%',
                        zIndex: 900,
                        transform: 'translate(-50%, -100%)',
                        pointerEvents: 'none'
                    }}
                >
                    <Icon
                        icon={isPinLocked ? 'solar:lock-keyhole-bold' : 'solar:map-point-bold'}
                        style={{
                            fontSize: '46px',
                            color: isPinLocked ? '#d97706' : '#ef4444',
                            filter: 'drop-shadow(0px 4px 8px rgba(0,0,0,0.35))'
                        }}
                    />
                </div>

                {/* Loading Overlay */}
                {isLoading && (
                    <div
                        style={{
                            position: 'absolute',
                            inset: 0,
                            zIndex: 950,
                            background: 'rgba(255,255,255,0.88)',
                            display: 'flex',
                            flexDirection: 'column',
                            alignItems: 'center',
                            justifyContent: 'center'
                        }}
                    >
                        <div className="spinner-border text-primary mb-2" role="status" />
                        <span style={{ fontSize: '13px', color: '#64748b', fontWeight: 500 }}>Loading Google Maps...</span>
                    </div>
                )}
            </div>

            {/* ===================================================================
                BOTTOM DOCK BAR: SELECTED VENUE LOCATION & QUICK ACTIONS
               =================================================================== */}
            <div
                style={{
                    padding: '10px 14px',
                    background: '#ffffff',
                    borderTop: '1px solid #e2e8f0',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'space-between',
                    gap: '12px',
                    flexWrap: 'wrap'
                }}
            >
                <div style={{ display: 'flex', alignItems: 'center', gap: '10px', minWidth: 0 }}>
                    <div
                        style={{
                            width: '34px',
                            height: '34px',
                            borderRadius: '8px',
                            background: '#eff6ff',
                            color: '#0789d5',
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'center',
                            flexShrink: 0
                        }}
                    >
                        <Icon icon="solar:map-point-wave-bold" style={{ fontSize: '18px' }} />
                    </div>
                    <div style={{ minWidth: 0 }}>
                        <div style={{ display: 'flex', alignItems: 'center', gap: '6px', flexWrap: 'wrap' }}>
                            <span style={{ fontSize: '10px', fontWeight: 700, color: '#64748b', textTransform: 'uppercase', letterSpacing: '0.5px' }}>
                                Marked Location
                            </span>
                            {selectedPlaceName && (
                                <span
                                    style={{
                                        fontSize: '10px',
                                        fontWeight: 600,
                                        background: '#e0f2fe',
                                        color: '#0369a1',
                                        padding: '1px 7px',
                                        borderRadius: '4px'
                                    }}
                                >
                                    {selectedPlaceName}
                                </span>
                            )}
                            <span
                                style={{
                                    fontSize: '11px',
                                    color: '#0789d5',
                                    fontFamily: 'monospace',
                                    background: '#f8fafc',
                                    border: '1px solid #e2e8f0',
                                    padding: '1px 6px',
                                    borderRadius: '4px'
                                }}
                            >
                                {location.lat.toFixed(6)}, {location.lng.toFixed(6)}
                            </span>
                        </div>
                        <div
                            style={{
                                fontSize: '13px',
                                fontWeight: 600,
                                color: '#0f172a',
                                whiteSpace: 'nowrap',
                                overflow: 'hidden',
                                textOverflow: 'ellipsis',
                                maxWidth: '460px'
                            }}
                        >
                            {geocodingAddress
                                ? ([geocodingAddress.street, geocodingAddress.city, geocodingAddress.country].filter(Boolean).join(', ') || 'Location pinned on map')
                                : 'Click or drag map to set venue address'}
                        </div>
                    </div>
                </div>

                {/* Action Buttons */}
                <div style={{ display: 'flex', alignItems: 'center', gap: '6px', flexWrap: 'wrap' }}>
                    <button
                        type="button"
                        onClick={handleSyncAddressNow}
                        style={{
                            ...btnBase,
                            height: '32px',
                            padding: '0 12px',
                            background: '#eff6ff',
                            color: '#0789d5',
                            border: '1px solid #bae6fd'
                        }}
                        title="Fill Street, City, State, Zip, Country fields from this pin"
                    >
                        <Icon icon="solar:check-read-bold" style={{ fontSize: '15px' }} />
                        <span>Fill Address Fields</span>
                    </button>

                    <button
                        type="button"
                        onClick={handleCopyShareLink}
                        style={{
                            ...btnBase,
                            height: '32px',
                            padding: '0 11px',
                            background: '#ffffff',
                            color: '#334155',
                            border: '1px solid #cbd5e1'
                        }}
                        title="Copy Google Maps share link"
                    >
                        <Icon icon="solar:copy-linear" style={{ fontSize: '14px' }} />
                        <span>Copy Link</span>
                    </button>

                    <a
                        href={`https://www.google.com/maps?q=${location.lat},${location.lng}`}
                        target="_blank"
                        rel="noopener noreferrer"
                        style={{
                            ...btnBase,
                            height: '32px',
                            padding: '0 11px',
                            background: '#ffffff',
                            color: '#334155',
                            border: '1px solid #cbd5e1',
                            textDecoration: 'none'
                        }}
                        title="Open this pin in Google Maps"
                    >
                        <Icon icon="solar:square-top-up-linear" style={{ fontSize: '14px' }} />
                        <span>Open in Google Maps</span>
                    </a>
                </div>
            </div>
        </div>
    );
};

export default MapPicker;
